import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { SlideDownAnimation } from '../animations';
import { Order } from '../../interfaces/orders/order';
import { ParsedBusinessReportEntity } from '../../interfaces/business-report-parsed-results/parsed-business-report-entity';
import { ParsedBusinessReportArticleClass } from '../../interfaces/business-report-parsed-results/parsed-business-report-article-information';
import { ParsedBusinessReportParty } from '../../interfaces/business-report-parsed-results/parsed-business-report-party';
import { ParsedBusinessReportRelationship } from '../../interfaces/business-report-parsed-results/parsed-business-report-relationship';
import { ParsedBusinessReportEvent } from '../../interfaces/business-report-parsed-results/parsed-business-report-event';
import { concatMap, filter, first, Observable, of, Subject, take, takeUntil, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { eNormalizedAddressType, eNormalizedEntityType, eNormalizedStatus, eOrderStatusType, eResultState } from '../../enums';
import { Utilities } from '../../utilities';
import { PageEvent } from '@angular/material/paginator';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

@Component({
    selector: 'lib-business-report-results',
    templateUrl: './business-report-results.component.html',
    styleUrl: './business-report-results.component.css',
    animations: [
        SlideDownAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessReportResultsComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() public order!: Order;
  @Input() public jurisdiction?: string;
  @Input() public results$: Observable<ParsedBusinessReportEntity | undefined> | undefined;
  @Input() public results: ParsedBusinessReportEntity | null | undefined;

  protected articleInformation: ParsedBusinessReportArticleClass[] = [];
  protected currentArticleInformationPage = 0;
  protected articleInformationPageSize = 5;

  protected parties: ParsedBusinessReportParty[] = [];
  protected currentPartyPage = 0;
  protected partyPageSize = 5;

  protected relationships: ParsedBusinessReportRelationship[] = [];
  protected currentRelationshipPage = 0;
  protected relationshipPageSize = 5;

  protected events: ParsedBusinessReportEvent[] = [];
  protected currentEventPage = 0;
  protected eventPageSize = 5;

  private onDestroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private ordersRepo: OrderRepositoryService,
    private searchResultsService: SearchResultsRepositoryService,
    private orderManager: OrderManagerService) { }

  ngOnInit() {
    if (this.results$) {
      this.results$.pipe(
        takeUntil(this.onDestroy$),
        first(res => !!res)
      ).subscribe(results => {
        this.results = results;
        this.initialize();
      });
    } else if (!this.order) {
      this.orderManager.currentOrder.pipe(
        takeUntil(this.onDestroy$),
        tap(order => this.order = order),
        first(order => order.orderStatusTypeID === eOrderStatusType.complete),
        concatMap(order => {
          if (order.searchResult?.resultState?.id === eResultState.noResult) {
            return of(null);
          } else if(this.results$) {
            return this.results$;
          } else if (order.searchResult?.hasParsedResult) {
            return this.searchResultsService.getBusinessReportParsedResults(order.id);
          }
          else {
            return of(null);
          }
        }))
        .subscribe(results => {
          this.results = results;
          this.initialize();
        });
    } else if(!this.results && this.order.searchResult?.hasParsedResult) {
      this.searchResultsService.getBusinessReportParsedResults(this.order.id).pipe(
        filter(() => this.order.orderStatusTypeID === eOrderStatusType.complete),
        takeUntil(this.onDestroy$)
      ).subscribe(results => {
        this.results = results;
        this.initialize();
      });
    } else {
      this.initialize();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initialize() {
    this.onPartyPaging();
    this.onRelationshipPaging();
    this.onEventPaging();
    this.onArticleInformationPaging();
  }
  
  shouldShow() {
    return this.results !== null && this.results !== undefined;
  }

  isBooleanSupplied(prop: boolean | null | undefined) {
    return prop !== null && prop !== undefined;
  }

  booleanToYesNo(prop: boolean) {
    return prop ? this.transloco.translate('common.buttons.yes') : this.transloco.translate('common.buttons.no');
  }

  spaceCase(val: string) {
    return Utilities.spacePascalCase(val);
  }

  getAddressType(type?: eNormalizedAddressType) {
    if (!type || type === eNormalizedAddressType.none) {
      return this.transloco.translate('common.labels.address');
    } else {
      return this.enumLabels.label('normalizedAddressType', type);
    }
  }

  formatAdditionalInformation(info: string) {
    if (this.order.jurisdiction === 'AB' &&
      info.startsWith('Share Structure')) {
      return info.replaceAll('\r\n', '<br />');
    } else {
      return info;
    }
  }

  getStatusIcon() {
    if (this.results?.normalizedStatus === eNormalizedStatus.active) {
      return 'check_circle_outline';
    } else if (this.results?.normalizedStatus === eNormalizedStatus.inactive) {
      return 'block';
    } else {
      return 'help';
    }
  }

  getStatusIconColor() {
    switch (this.results?.normalizedStatus) {
      case eNormalizedStatus.active:
        return { color: '#5C7F46' };
      case eNormalizedStatus.inactive:
        return { color: '#FB2C38' };
      case eNormalizedStatus.notProvided:
        return { color: '#2F96B4' };
      default: return { color: 'black' };
    }
  }

  getTypeIcon() {
    if (this.results?.normalizedEntityType === eNormalizedEntityType.businessName) {
      return 'domain';
    } else if (this.results?.normalizedEntityType === eNormalizedEntityType.corporation) {
      return 'finance';
    } else {
      return '';
    }
  }

  onPartyPaging(e?: PageEvent) {
    this.currentPartyPage = e?.pageIndex ?? this.currentPartyPage;

    const start = this.currentPartyPage * this.partyPageSize;
    const end = start + this.partyPageSize;
    this.parties = this.results?.parties?.slice(start, end) ?? [];
  }

  onRelationshipPaging(e?: PageEvent) {
    this.currentRelationshipPage = e?.pageIndex ?? this.currentRelationshipPage;

    const start = this.currentRelationshipPage * this.relationshipPageSize;
    const end = start + this.relationshipPageSize;
    this.relationships = this.results?.relationships?.slice(start, end) ?? [];
  }

  onEventPaging(e?: PageEvent) {
    this.currentEventPage = e?.pageIndex ?? this.currentEventPage;

    const start = this.currentEventPage * this.eventPageSize;
    const end = start + this.eventPageSize;
    this.events = this.results?.events?.slice(start, end) ?? [];
  }

  onArticleInformationPaging(e?: PageEvent) {
    this.currentArticleInformationPage = e?.pageIndex ?? this.currentArticleInformationPage;

    const start = this.currentArticleInformationPage * this.articleInformationPageSize;
    const end = start + this.articleInformationPageSize;
    this.articleInformation = this.results?.articleInformation?.slice(start, end) ?? [];
  }
}
